import React from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-center gap-2.5">
          <img src="/logo.svg" alt="Banyak Group" className="h-8 w-8" />
          <span className="text-lg font-bold text-gray-900 tracking-tight">Platform</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/client/login')}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
            Client Portal
          </button>
          <button onClick={() => navigate('/login')}
            className="px-5 py-2 text-sm font-medium bg-emerald-800 text-white rounded-lg hover:bg-emerald-900 transition-colors">
            Sign In
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
            Recruitment Pipeline Management
          </div>
          <h1 className="text-5xl font-extrabold text-gray-900 leading-tight tracking-tight">
            Hire smarter for
            <span className="text-emerald-800"> investment firms</span>
          </h1>
          <p className="mt-6 text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto">
            Banyak Group gives recruitment teams and their clients a single platform to manage pipelines, 
            coordinate interviews, and move candidates from sourcing to placement — together.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <button onClick={() => navigate('/login')}
              className="px-7 py-3 text-sm font-semibold bg-emerald-800 text-white rounded-lg hover:bg-emerald-900 transition-colors shadow-lg shadow-emerald-900/20">
              Go to Dashboard
            </button>
            <button onClick={() => navigate('/client/login')}
              className="px-7 py-3 text-sm font-semibold bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Client Login
            </button>
          </div>
        </div>
      </section>

      {/* Product Mockup - Client Interview Stages */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="bg-gray-950 rounded-2xl p-2 shadow-2xl">
          <div className="bg-gray-900 rounded-xl overflow-hidden">
            {/* Browser bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-800 border-b border-gray-700">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/70"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/70"></div>
              </div>
              <div className="flex-1 flex justify-center">
                <div className="bg-gray-700 rounded-md px-4 py-1 text-[10px] text-gray-400 font-mono">app.banyak.group</div>
              </div>
            </div>
            {/* Mock kanban */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-white text-sm font-semibold">Client Interview Pipeline</span>
                </div>
                <span className="text-gray-500 text-xs">6 candidates in process</span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { stage: 'New for Review', color: 'bg-blue-500', cards: [{ name: 'Sarah K.', title: 'VP Operations · Goldman', stars: 4 }, { name: 'Michael R.', title: 'Director · Blackstone', stars: 0 }] },
                  { stage: 'Round 1', color: 'bg-amber-500', cards: [{ name: 'James T.', title: 'MD · Morgan Stanley', stars: 5 }] },
                  { stage: 'Final Round', color: 'bg-purple-500', cards: [{ name: 'Emily C.', title: 'Partner · KKR', stars: 4 }, { name: 'David L.', title: 'SVP · Citadel', stars: 3 }] },
                  { stage: 'Offer', color: 'bg-emerald-500', cards: [{ name: 'Rachel M.', title: 'Director · Apollo', stars: 5 }] }
                ].map((col, ci) => (
                  <div key={ci} className="bg-gray-800/50 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-2.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${col.color}`}></div>
                      <span className="text-gray-400 text-[10px] font-medium uppercase tracking-wide">{col.stage}</span>
                      <span className="text-gray-600 text-[10px] ml-auto">{col.cards.length}</span>
                    </div>
                    <div className="space-y-2">
                      {col.cards.map((card, ki) => (
                        <div key={ki} className="bg-gray-800 border border-gray-700/50 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0">
                              {card.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="min-w-0">
                              <p className="text-white text-[11px] font-medium truncate">{card.name}</p>
                              <p className="text-gray-500 text-[9px] truncate">{card.title}</p>
                            </div>
                          </div>
                          {card.stars > 0 && (
                            <div className="flex gap-0.5 mt-1.5">
                              {[1,2,3,4,5].map(s => (
                                <span key={s} className={`text-[8px] ${s <= card.stars ? 'text-yellow-400' : 'text-gray-700'}`}>★</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">How It Works</p>
            <h2 className="text-3xl font-bold text-gray-900">Three steps to a better hiring process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Build Your Pipeline', desc: 'Recruiters source candidates, build detailed profiles, and organize them into visual kanban stages. Track every candidate from initial contact to placement.', icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
              )},
              { step: '02', title: 'Present to Clients', desc: 'Share candidates with client firms through a dedicated portal. Clients review profiles, rate resumes, and move candidates through their own interview stages.', icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              )},
              { step: '03', title: 'Schedule & Close', desc: 'Coordinate interviews with Google Calendar and Outlook. Track feedback, manage offers, and move candidates to placement — all in one platform.', icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              )}
            ].map((item, i) => (
              <div key={i}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-800 text-white flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="text-[10px] font-bold text-emerald-700/40 uppercase tracking-widest">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">Who It's For</p>
          <h2 className="text-3xl font-bold text-gray-900">Built for both sides of the table</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="relative bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-8 text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-700/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Recruitment Firms</h3>
              <p className="text-emerald-200 text-sm leading-relaxed mb-5">Manage your full candidate pipeline from sourcing to placement. Build profiles, track stages, share candidates with clients, and coordinate the entire interview process.</p>
              <ul className="space-y-2">
                {['Kanban pipeline boards', 'Candidate profiles & documents', 'Client presentation workflow', 'Status reports & tracking'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-emerald-100">
                    <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="relative bg-gray-50 border border-gray-200 rounded-2xl p-8 overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-gray-200/50 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Client Firms</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">Review candidates presented by your recruitment partner. Rate resumes, manage interview stages, provide feedback, and track the entire hiring process through your own portal.</p>
              <ul className="space-y-2">
                {['Dedicated client portal', 'Interview stage management', 'Candidate rating & feedback', 'Calendar integration & scheduling'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-widest mb-2">Features</p>
            <h2 className="text-3xl font-bold text-gray-900">Everything you need to manage recruitment</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Pipeline Management', desc: 'Visual kanban boards with drag-and-drop stages, SLA tracking, and candidate code assignment.', color: 'bg-blue-100 text-blue-700', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" /> },
              { title: 'Client Portal', desc: 'Dedicated portal for clients to review candidates, rate resumes, and provide interview feedback.', color: 'bg-emerald-100 text-emerald-700', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /> },
              { title: 'Calendar Integration', desc: 'Schedule interviews via Google Calendar and Outlook with automatic Meet and Teams links.', color: 'bg-purple-100 text-purple-700', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /> },
              { title: 'Candidate Profiles', desc: 'Comprehensive profiles with employment history, compensation data, documents, and presentation files.', color: 'bg-amber-100 text-amber-700', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /> },
              { title: 'Status Reports', desc: 'Track candidate progress across searches with status reports shared between recruiters and clients.', color: 'bg-rose-100 text-rose-700', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /> },
              { title: 'Email Notifications', desc: 'Automatic email updates when candidates are presented, rated, approved, or moved through stages.', color: 'bg-cyan-100 text-cyan-700', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /> },
            ].map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
                <div className={`w-10 h-10 rounded-lg ${f.color} flex items-center justify-center mb-4`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">{f.icon}</svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1.5">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 30% 50%, white 0%, transparent 70%)'}}></div>
          <div className="relative">
            <h2 className="text-3xl font-bold text-white mb-3">Ready to streamline your hiring?</h2>
            <p className="text-emerald-200 max-w-lg mx-auto mb-8">Join recruitment firms that manage their entire candidate pipeline and client collaboration in one platform.</p>
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => navigate('/login')}
                className="px-7 py-3 text-sm font-semibold bg-white text-emerald-900 rounded-lg hover:bg-gray-100 transition-colors">
                Sign In to Dashboard
              </button>
              <a href="mailto:oban@banyak.group"
                className="px-7 py-3 text-sm font-semibold text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors inline-block">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="Banyak Group" className="h-5 w-5 opacity-40" />
              <span className="text-xs text-gray-400">&copy; {new Date().getFullYear()} Banyak Group. All rights reserved.</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <a href="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</a>
              <span>&middot;</span>
              <a href="/terms" className="hover:text-gray-600 transition-colors">Terms of Service</a>
              <span>&middot;</span>
              <a href="/messaging" className="hover:text-gray-600 transition-colors">Messaging Program</a>
              <span>&middot;</span>
              <a href="mailto:oban@banyak.group" className="hover:text-gray-600 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
